import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, FormControlLabel, Switch,
} from '@mui/material';
import { HTagsTextField } from 'BaseComponents';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import {
  cleanRoomQuestionPermissions,
  crqPermissionsAccessLevels,
} from 'components/CleanRooms/constants';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';


const StyledAccordion = styled(Accordion)({
  '&:before': {
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    backgroundColor: 'transparent',
  },
  borderColor: 'transparent',
});

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
}));

export const QuestionPermissionsItem = ({
  permission, permissions, setPermissions, orgList = [], exdenedPartnerPermissions = [],
  setValidation,
}) => {
  const { ID: orgId } = useSelector(activeOrgSelector);
  const [expand, setExpand] = useState(false);

  const orgListMap = useMemo(() => (orgList
    .reduce((acc, item) => ({ ...acc, [item.organizationID]: item.name }), {})
  ), [orgList]);

  useEffect(() => {
    let needUpdate = false;
    const newPermissions = (permissions ?? []).map(p => {
      let newPartners = [];
      if (p.access === crqPermissionsAccessLevels.CLEAN_ROOM_PARTNER) {
        newPartners = (p.partners ?? []).map((partner) => {
          if (typeof partner === 'string') {
            needUpdate = orgListMap[partner];
            return {
              ID: partner,
              value: orgListMap[partner],
            };
          }
          return partner;
        });
      }
      return {
        ...p,
        partners: newPartners,
      };
    });
    if (needUpdate) {
      setPermissions(newPermissions);
    }
  }, [orgListMap, permissions, setPermissions]);

  const currnetPermission = useMemo(() => {
    const perm = permissions.find(p => p.permission === permission.name);
    if (perm) {
      return {
        ...perm,
      };
    }
    return {
      ID: 0,
      permission: permission.name,
      access: crqPermissionsAccessLevels.QUESTION_OWNER_ONLY,
      partners: [],
    };
  }, [permissions, permission.name]);

  const updatePermissions = (permissionName, access, partners = []) => {
    if (setValidation) {
      if (access === crqPermissionsAccessLevels.CLEAN_ROOM_PARTNER) {
        const isValid = partners.length > 0;
        setValidation(isValid);
      }
      else {
        setValidation(true);
      }
    }

    setPermissions((prevState) => [
      ...prevState.filter(p => p.permission !== permissionName),
      {
        permission: permissionName,
        access,
        partners,
      },
    ]);
  };

  const isPermissionChecked = useMemo(() => [
    crqPermissionsAccessLevels.ALL_CLEAN_ROOM_PARTNERS,
    crqPermissionsAccessLevels.CLEAN_ROOM_PARTNER].includes(currnetPermission?.access),
  [currnetPermission?.access]);

  const isPermissionSwitchDisabled = (currentPermission, currentQuestionPermissions) => {
    if (currentPermission.name !== cleanRoomQuestionPermissions.VIEW_CODE.name) {
      return false;
    }
    const editPermission = currentQuestionPermissions
      .find(p => p.permission === cleanRoomQuestionPermissions.EDIT.name);
    if (editPermission
      && [
        crqPermissionsAccessLevels.ALL_CLEAN_ROOM_PARTNERS,
        crqPermissionsAccessLevels.CLEAN_ROOM_PARTNER].includes(editPermission.access)) {
      return true;
    }
    return false;
  };

  const handleSwitchOnChangePermissions = (permissionName, checked) => {
    if (permissionName === cleanRoomQuestionPermissions.EDIT.name && checked) {
      handleSwitchOnChangePermissions(cleanRoomQuestionPermissions.VIEW_CODE.name, checked);
    }

    const access = checked ? crqPermissionsAccessLevels.ALL_CLEAN_ROOM_PARTNERS
      : crqPermissionsAccessLevels.QUESTION_OWNER_ONLY;
    updatePermissions(permissionName, access);
  };

  const handleOnChangeRadioGroup = (event) => {
    const access = event.target.value;
    updatePermissions(currnetPermission.permission, access);
  };

  const organizationsOptions = useMemo(() => {
    const newArr = orgList.filter(({ organizationID }) => organizationID !== orgId)
      .map(org => org.name);
    return newArr || [];
  }, [orgId, orgList]);

  const handlePartnersInput = (orgName) => {
    const selectedOrg = orgList.find(o => o.name === orgName);
    updatePermissions(
      currnetPermission.permission,
      currnetPermission.access,
      [...currnetPermission.partners, { ID: selectedOrg.organizationID, value: orgName }],
    );
  };

  const handleOnDeleteAll = () => {
    updatePermissions(currnetPermission.permission, currnetPermission.access);
  };

  const handleOnDelete = (e) => {
    const newPartners = currnetPermission.partners.filter(o => o.value !== e.removeText);
    updatePermissions(
      currnetPermission.permission,
      currnetPermission.access,
      newPartners,
    );
  };

  const exdenedPermissionsOptions = exdenedPartnerPermissions.includes(permission.name);

  return (
    <>
      <FormControlLabel
        label={permission.displayName}
        labelPlacement='start'
        sx={{ justifyContent: 'space-between' }}
        control={
          (
            <Switch
              checked={isPermissionChecked}
              onChange={e => handleSwitchOnChangePermissions(permission.name, e.target.checked)}
              disabled={isPermissionSwitchDisabled(permission, permissions)}
            />
          )
        }
      />
      {exdenedPermissionsOptions && (
        <StyledAccordion
          variant='outlined'
          sx={{ p: 0 }}
          expanded={expand && isPermissionChecked}
          onChange={() => setExpand(!expand)}
          disabled={!isPermissionChecked}
        >
          <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
            Select Partners
          </StyledAccordionSummary>
          <AccordionDetails>
            <RadioGroup
              aria-labelledby='require-join-radio'
              name='requireJoins'
              value={currnetPermission.access}
              onChange={handleOnChangeRadioGroup}
            >
              <Box mx={4}>
                <FormControlLabel
                  value={crqPermissionsAccessLevels.CLEAN_ROOM_PARTNER}
                  label='Only the following partners'
                  control={<Radio size='small' />}
                  disabled={!isPermissionChecked}
                />
                <Box mx={3} my={1}>
                  <HTagsTextField
                    value={currnetPermission.partners}
                    onChange={handlePartnersInput}
                    onDeleteAll={handleOnDeleteAll}
                    onDelete={handleOnDelete}
                    options={organizationsOptions}
                    size='small'
                    chipProps={{
                      size: 'small',
                    }}
                    disabled={
                      !isPermissionChecked
                      || currnetPermission.access !== crqPermissionsAccessLevels.CLEAN_ROOM_PARTNER
                    }
                  />
                </Box>
              </Box>
              <Box mx={4}>
                <FormControlLabel
                  value={crqPermissionsAccessLevels.ALL_CLEAN_ROOM_PARTNERS}
                  label='Any partner'
                  control={<Radio size='small' />}
                  disabled={!isPermissionChecked}
                />
              </Box>
            </RadioGroup>
          </AccordionDetails>
        </StyledAccordion>
      )}
    </>
  );
};
